/**
 * Text a database can actually hold.
 *
 * PostgreSQL refuses two classes of string that JavaScript carries happily, in
 * `text` columns and in `jsonb` alike: the NUL character, and an unpaired
 * surrogate. Neither has a representation in the server encoding, so there is
 * no version of "store it faithfully" available.
 *
 * That matters here because of what the alternative costs. A play whose text
 * reaches the insert unrepresentable throws a driver error, a driver error is
 * not an `AppError`, and the error envelope maps anything it does not recognise
 * to INTERNAL. On the sync route a 500 means the device retries the identical
 * batch forever and never lands another play for the rest of the game.
 *
 * So the choice is REPLACE or REFUSE, and replace wins, which is the opposite
 * of the call this codebase makes about play content. The difference is what
 * each one costs: refusing a play that names a bad player id costs that play,
 * while refusing a coach's note costs the whole outbox queued behind it. The
 * replacement character is the Unicode standard's own answer to a character
 * that cannot be represented, and losing one glyph of free text beats losing a
 * game.
 *
 * The reachable input is ordinary rather than adversarial: `MAX_META_BYTES`
 * invites a client to truncate a long note, and truncating a JavaScript string
 * that ends in an emoji splits the surrogate pair. Found by a fresh-context
 * pass, measured end to end, and the third time on this branch that a store
 * constraint had no counterpart at the door.
 *
 * Written with codepoint arithmetic rather than regex escapes on purpose. A
 * source file describing surrogates is one careless edit away from CONTAINING
 * one, and this file has already been rewritten once for that reason.
 */

const REPLACEMENT = String.fromCharCode(0xfffd);

const LEAD_FIRST = 0xd800;
const LEAD_LAST = 0xdbff;
const TRAIL_FIRST = 0xdc00;
const TRAIL_LAST = 0xdfff;

function isLead(code: number): boolean {
  return code >= LEAD_FIRST && code <= LEAD_LAST;
}
function isTrail(code: number): boolean {
  return code >= TRAIL_FIRST && code <= TRAIL_LAST;
}

/**
 * NUL removed, unpaired surrogates replaced.
 *
 * NUL is REMOVED rather than replaced, because it is never content: it arrives
 * from a C string boundary or a truncation bug, and standing a visible glyph in
 * for it would put a character on a coach's screen that nobody typed. A broken
 * emoji IS content, badly cut, so it keeps its place in the sentence.
 */
export function toStorableText(value: string): string {
  let out = '';
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);

    if (code === 0) continue;

    if (isLead(code)) {
      const next = i + 1 < value.length ? value.charCodeAt(i + 1) : -1;
      if (isTrail(next)) {
        // A whole character. Copy both halves and step over the pair.
        out += value[i];
        out += value[i + 1];
        i += 1;
        continue;
      }
      out += REPLACEMENT;
      continue;
    }

    if (isTrail(code)) {
      // A trailing half with no lead before it: the other side of a bad cut.
      out += REPLACEMENT;
      continue;
    }

    out += value[i];
  }
  return out;
}

/** True when a string is already storable, which is the overwhelming case. */
export function isStorableText(value: string): boolean {
  return toStorableText(value) === value;
}

/**
 * The same rule applied through a JSON value, keys included.
 *
 * A `jsonb` column refuses the character wherever it appears, so a note nested
 * three levels down is exactly as fatal as one at the top.
 *
 * The work is bounded by the caller, and for three rounds this comment said it
 * was bounded by `MAX_META_BYTES` when it was not: that cap ran AFTER this
 * transform, so the only real limit was the 1mb body parser and a crafted
 * `meta` could hold the event loop for tens of seconds. `GameEventInputSchema`
 * now measures the value before calling this. A future caller that does not
 * must impose its own bound: nothing in this function does.
 *
 * Deliberately total, and "total" is not "identity". A number, a boolean, null
 * and undefined come back as they went in. Anything else with `typeof`
 * `'object'` is WALKED, including a `Date` or a `Map`, which have no own
 * enumerable properties and therefore come back as `{}`. An earlier version of
 * this sentence said everything that is not a string, an array or a plain
 * object comes back unchanged, which is false for exactly those, and a delta
 * pass caught it here after the same claim had been corrected in the schema.
 *
 * That behaviour is fine because the schema refuses those shapes before this
 * runs. It is stated rather than assumed because a future caller without that
 * door in front of it would lose data and never know.
 *
 * Two keys can sanitise to the same string: `a` and `a<NUL>` both come out as
 * `a`. A colliding key grows a replacement character until it is unique,
 * because the alternative is last-write-wins, which is silent loss of a value
 * on the route that may not lose content. Badly named beats gone.
 */
export function toStorableJson(value: unknown): unknown {
  if (typeof value === 'string') return toStorableText(value);
  if (Array.isArray(value)) return value.map(toStorableJson);
  if (typeof value === 'object' && value !== null) {
    const out: Record<string, unknown> = {};
    /*
     * How many replacements each base key has already spent.
     *
     * Without it the loop below restarts from the base every time, so the nth
     * key that collides walks n growing strings and the whole object costs
     * cubic time in the number of colliding keys. That was not theoretical:
     * 5151 keys that all sanitise to the same string fit in a 603KB body and
     * held the event loop for 26.8 seconds. The size bound in
     * `GameEventInputSchema` is the real fix, since it caps the key count long
     * before this matters; this map is here so the shape of the loop is not
     * quadratic on its own terms, because the next caller may not have a cap.
     */
    const spent = new Map<string, number>();
    for (const [key, nested] of Object.entries(value)) {
      const base = toStorableText(key);
      let count = spent.get(base) ?? 0;
      let storable = count === 0 ? base : base + REPLACEMENT.repeat(count);
      while (Object.hasOwn(out, storable)) {
        count += 1;
        storable = base + REPLACEMENT.repeat(count);
      }
      spent.set(base, count + 1);
      /*
       * `defineProperty`, not `out[storable] =`, because `__proto__` is a key a
       * JSON body can carry.
       *
       * Plain assignment invokes the inherited `__proto__` setter rather than
       * creating an own property: a string value is discarded outright, an
       * object value silently becomes this object's prototype, and either way
       * `JSON.stringify` never sees it. So a note nested under that key was
       * accepted with a 200 and stored without its text, on the route that may
       * not lose content. Found independently by three review passes.
       *
       * A regression introduced by the collision fix above: the
       * `Object.fromEntries` version it replaced used these same
       * CreateDataProperty semantics and kept the key.
       *
       * NOT a null-prototype accumulator, which was the first attempt and is
       * how this comment came to be so long. `Object.create(null)` fixes the
       * key and breaks the write: the object is not `instanceof Object` and has
       * no `toString`, so the driver's parameter coercion throws "Cannot
       * convert object to primitive value", which is not an `AppError` and
       * therefore a 500 on the sync route, which is a device retrying forever.
       * Six route tests caught it. The lesson is the one this file already
       * records: the fix for silent loss must not become loud loss.
       */
      Object.defineProperty(out, storable, {
        value: toStorableJson(nested),
        enumerable: true,
        writable: true,
        configurable: true,
      });
    }
    return out;
  }
  return value;
}
